import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddScheduleItemOptions,
  AddScheduleItemResponse,
  DeleteScheduleItemOptions,
  GetScheduleItemOptions,
  ItemsQueryBuilder,
  ListScheduleItemsOptions,
  ListScheduleItemsResponse,
  QueryScheduleItemsApplicationErrors,
  QueryScheduleItemsResponse,
  RescheduleDraftOptions,
  ScheduleItem,
  ScheduleItemQuery,
  UpdateScheduleItemOptions,
  UpdateScheduleItemResponse,
  addScheduleItem as universalAddScheduleItem,
  deleteScheduleItem as universalDeleteScheduleItem,
  discardDraft as universalDiscardDraft,
  getScheduleItem as universalGetScheduleItem,
  listScheduleItems as universalListScheduleItems,
  publishDraft as universalPublishDraft,
  queryScheduleItems as universalQueryScheduleItems,
  rescheduleDraft as universalRescheduleDraft,
  typedQueryScheduleItems as universalTypedQueryScheduleItems,
  updateScheduleItem as universalUpdateScheduleItem,
} from './events-schedule-v1-schedule-item-schedule.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function listScheduleItems(
  httpClient: HttpClient
): ListScheduleItemsSignature {
  return (options?: ListScheduleItemsOptions) =>
    universalListScheduleItems(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListScheduleItemsSignature {
  /**
   * Retrieves a list of up to 100 schedule items, with basic filter support.
   *
   * Default filters to schedule items with a `state` of `PUBLISHED` and `VISIBLE`.
   * @param - Optional fields.
   */
  (options?: ListScheduleItemsOptions): Promise<
    NonNullablePaths<
      ListScheduleItemsResponse,
      | `total`
      | `limit`
      | `offset`
      | `items`
      | `items.${number}._id`
      | `items.${number}.hidden`
      | `items.${number}.name`
      | `items.${number}.description`
      | `items.${number}.stageName`
      | `items.${number}.status`
      | `items.${number}.eventId`
      | `items.${number}.draft`,
      4
    >
  >;
}

export function queryScheduleItems(
  httpClient: HttpClient
): QueryScheduleItemsSignature {
  return () =>
    universalQueryScheduleItems(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryScheduleItemsSignature {
  /**
   * Creates a query to retrieve a list of schedule items.
   *
   * The `queryScheduleItems( )` method builds a query to retrieve a list of schedule items and returns a `ItemsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/events/schedule/items-query-builder/find) method.
   *
   * You can refine the query by chaining `ItemsQueryBuilder` methods onto the query. `ItemsQueryBuilder` methods enable you to sort, filter, and control the results `queryScheduleItems( )` returns.
   *
   * `queryScheduleItems( )` runs with these `ItemsQueryBuilder` defaults, which you can override:
   *
   *  - [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/events/schedule/items-query-builder/limit)
   *  - [`descending("_createdDate")`](https://dev.wix.com/docs/sdk/backend-modules/events/schedule/items-query-builder/descending)
   *
   * The methods that are chained to `queryScheduleItems( )` are applied in the order they're called. For example, if you apply `ascending('name')` and then `descending('stageName')`, the results are sorted first by the `name`, and then, if there are multiple results with the same `name`, the items are sorted by `stageName`.
   */
  (): ItemsQueryBuilder;
}

export function typedQueryScheduleItems(
  httpClient: HttpClient
): TypedQueryScheduleItemsSignature {
  return (query: ScheduleItemQuery) =>
    universalTypedQueryScheduleItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryScheduleItemsSignature {
  /** */
  (query: ScheduleItemQuery): Promise<
    NonNullablePaths<
      QueryScheduleItemsResponse,
      | `items`
      | `items.${number}._id`
      | `items.${number}.hidden`
      | `items.${number}.name`
      | `items.${number}.description`
      | `items.${number}.stageName`
      | `items.${number}.status`
      | `items.${number}.eventId`
      | `items.${number}.draft`,
      4
    > & {
      __applicationErrorsType?: QueryScheduleItemsApplicationErrors;
    }
  >;
}

export function getScheduleItem(
  httpClient: HttpClient
): GetScheduleItemSignature {
  return (itemId: string, options?: GetScheduleItemOptions) =>
    universalGetScheduleItem(
      itemId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetScheduleItemSignature {
  /**
   * Retrieves a schedule item.
   * @param - Schedule item ID.
   * @param - Optional fields.
   * @returns Schedule item.
   */
  (itemId: string, options?: GetScheduleItemOptions): Promise<
    NonNullablePaths<
      ScheduleItem,
      | `_id`
      | `hidden`
      | `name`
      | `description`
      | `stageName`
      | `tags`
      | `status`
      | `eventId`
      | `draft`,
      2
    >
  >;
}

export function addScheduleItem(
  httpClient: HttpClient
): AddScheduleItemSignature {
  return (
    eventId: string,
    options?: NonNullablePaths<
      AddScheduleItemOptions,
      | `item.name`
      | `item.timeSlot`
      | `item.timeSlot.end`
      | `item.timeSlot.start`,
      4
    >
  ) =>
    universalAddScheduleItem(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddScheduleItemSignature {
  /**
   * Adds a schedule item to a draft schedule.  For the same date and time you can have multiple items.
   *
   * Note that draft items are not publicly available unless published.
   * @param - Event ID to add the schedule item to.
   * @param - Optional fields.
   */
  (
    eventId: string,
    options?: NonNullablePaths<
      AddScheduleItemOptions,
      | `item.name`
      | `item.timeSlot`
      | `item.timeSlot.end`
      | `item.timeSlot.start`,
      4
    >
  ): Promise<
    NonNullablePaths<
      AddScheduleItemResponse,
      | `item._id`
      | `item.hidden`
      | `item.name`
      | `item.description`
      | `item.stageName`
      | `item.tags`
      | `item.status`
      | `item.eventId`
      | `item.draft`,
      3
    >
  >;
}

export function updateScheduleItem(
  httpClient: HttpClient
): UpdateScheduleItemSignature {
  return (
    itemId: string,
    eventId: string,
    options?: UpdateScheduleItemOptions
  ) =>
    universalUpdateScheduleItem(
      itemId,
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateScheduleItemSignature {
  /**
   * Updates an existing schedule item.
   *
   * The changes are performed on a draft schedule item, even if the schedule item has already been published.
   * @param - Schedule item ID to update.
   * @param - Event ID to which the schedule item belongs.
   * @param - Optional fields.
   */
  (
    itemId: string,
    eventId: string,
    options?: UpdateScheduleItemOptions
  ): Promise<
    NonNullablePaths<
      UpdateScheduleItemResponse,
      | `item._id`
      | `item.hidden`
      | `item.name`
      | `item.description`
      | `item.stageName`
      | `item.tags`
      | `item.status`
      | `item.eventId`
      | `item.draft`,
      3
    >
  >;
}

export function deleteScheduleItem(
  httpClient: HttpClient
): DeleteScheduleItemSignature {
  return (eventId: string, options?: DeleteScheduleItemOptions) =>
    universalDeleteScheduleItem(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteScheduleItemSignature {
  /**
   * Deletes a schedule item from a draft schedule.
   * @param - Event ID to which the schedule item belongs.
   * @param - Optional fields.
   */
  (eventId: string, options?: DeleteScheduleItemOptions): Promise<void>;
}

export function discardDraft(httpClient: HttpClient): DiscardDraftSignature {
  return (eventId: string) =>
    universalDiscardDraft(
      eventId,
      // @ts-ignore
      { httpClient }
    );
}

interface DiscardDraftSignature {
  /**
   * Clears all changes to a draft schedule.
   * @param - Event ID to which the draft schedule item belongs.
   */
  (eventId: string): Promise<void>;
}

export function publishDraft(httpClient: HttpClient): PublishDraftSignature {
  return (eventId: string) =>
    universalPublishDraft(
      eventId,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishDraftSignature {
  /**
   * Publishes a draft schedule.
   * @param - Event ID to which the draft schedule item belongs.
   */
  (eventId: string): Promise<void>;
}

export function rescheduleDraft(
  httpClient: HttpClient
): RescheduleDraftSignature {
  return (
    eventId: string,
    options?: NonNullablePaths<RescheduleDraftOptions, `timeZoneId`, 2>
  ) =>
    universalRescheduleDraft(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RescheduleDraftSignature {
  /**
   * Adjusts the time of all draft schedule items for an event at once.
   * @param - Event ID to which the draft schedule item belongs.
   * @param - Optional fields.
   */
  (
    eventId: string,
    options?: NonNullablePaths<RescheduleDraftOptions, `timeZoneId`, 2>
  ): Promise<void>;
}

export {
  AddScheduleItemOptions,
  AddScheduleItemRequest,
  AddScheduleItemResponse,
  CreateBookmarkRequest,
  CreateBookmarkResponse,
  Cursors,
  DeleteBookmarkRequest,
  DeleteBookmarkResponse,
  DeleteScheduleItemOptions,
  DeleteScheduleItemRequest,
  DeleteScheduleItemResponse,
  DiscardDraftRequest,
  DiscardDraftResponse,
  FacetCounts,
  GetScheduleItemOptions,
  GetScheduleItemRequest,
  GetScheduleItemResponse,
  ItemsQueryBuilder,
  ItemsQueryResult,
  ListBookmarksRequest,
  ListBookmarksResponse,
  ListScheduleItemsOptions,
  ListScheduleItemsRequest,
  ListScheduleItemsResponse,
  Paging,
  PagingMetadataV2,
  PublishDraftRequest,
  PublishDraftResponse,
  QueryScheduleItemsRequest,
  QueryScheduleItemsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RescheduleDraftOptions,
  RescheduleDraftRequest,
  RescheduleDraftResponse,
  ScheduleItem,
  ScheduleItemData,
  ScheduleItemQuerySpec,
  ScheduleStatus,
  SortOrder,
  Sorting,
  StateFilter,
  TimeInterval,
  UpdateScheduleItemOptions,
  UpdateScheduleItemRequest,
  UpdateScheduleItemResponse,
  utils,
} from './events-schedule-v1-schedule-item-schedule.universal.js';
